import { PageProps } from '../../shell/shell';
import { fullImage, imgUrl } from '../../utils/images';
import { format, isSameMonth, isSameYear, parseISO } from 'date-fns';
import slugify from 'slugify';
import { StoryBox } from './storyBox';
import { useEffect, useState } from 'react';
import { Story, Trip } from '../../schema';
import NotFoundPage from '../notFound/notFoundPage';
import { useLocation } from 'react-router-dom';
import { useTripVisitStats } from '../../hooks/tripVisitStats';
import {
  useLazyGetInteractionsByTripByTripIdAndLinkSecretQuery,
  useLazyGetStoriesByTripByTripIdQuery,
  useLazyGetTripsByTripIdQuery,
} from '../../store/backendApi';

interface TripData {
  trip: Trip;
  stories: Story[];
}

const getLinkSecret = (path: string): string | null =>
  path.includes('/trip/_s_') ? path.split('/trip/')[1]?.replaceAll('/', '') || null : null;

export const TripPage = (props: PageProps) => {
  const [data, setData] = useState<TripData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const linkSecret = getLinkSecret(location.pathname);
  useTripVisitStats(data, linkSecret);
  const [loadInteractions, { data: interactions }] =
    useLazyGetInteractionsByTripByTripIdAndLinkSecretQuery();

  const [loadTrip] = useLazyGetTripsByTripIdQuery();
  const [loadStories] = useLazyGetStoriesByTripByTripIdQuery();

  const isOwner =
    !!data?.trip?.ownerId && !!props.user?.userId && data.trip.ownerId === props.user?.userId;

  useEffect(() => {
    const loadDataViaApi = async () => {
      const tripId = location.pathname.split('/p/trip/')[1].replaceAll('/', '');
      const [tripResponse, storiesResponse] = await Promise.all([
        loadTrip(tripId),
        loadStories(tripId),
      ]);
      const tripData = tripResponse.data;
      const storiesData = storiesResponse.data;
      if (!tripData || !storiesData) {
        throw new Error('failed to fetch data');
      }
      setData({
        trip: tripData,
        stories: [...storiesData.stories],
      });
    };
    const loadStaticData = async () => {
      const response = await fetch('./data.json');
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const jsonData = await response.json();
      setData(jsonData);
    };

    const loadDataFunc = location.pathname.includes('/p/trip/') ? loadDataViaApi : loadStaticData;

    (async () => {
      try {
        await loadDataFunc();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    })();
  }, [location.pathname]);

  useEffect(() => {
    if (!data || !data.trip || !data.trip.id) {
      return;
    }
    loadInteractions({
      tripId: data.trip.id,
      linkSecret: linkSecret ?? 'none',
    });
  }, [data]);

  // Scroll to anchor functionality
  useEffect(() => {
    const handleScrollToAnchor = () => {
      const hash = window.location.hash;
      if (!hash || !data?.stories) {
        return;
      }

      // Remove the # from the hash
      const anchorId = hash.substring(1);

      // Find the story that matches this anchor
      const matchingStory = data.stories.find((story) => {
        const storyAnchor = slugify(story.title, {
          lower: true,
          strict: true,
        }).slice(0, 50);
        return storyAnchor === anchorId;
      });

      if (matchingStory) {
        // Wait a bit for the DOM to be fully rendered
        const scrollToElement = () => {
          const element = document.getElementById(anchorId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          } else {
            // If element not found, try again after a short delay
            setTimeout(scrollToElement, 100);
          }
        };

        // Small delay to ensure stories are rendered
        setTimeout(scrollToElement, 200);
      }
    };

    // Only run if we have data and stories
    if (data?.stories && data.stories.length > 0) {
      handleScrollToAnchor();
    }

    // Listen for hash changes (when user clicks on story titles)
    const handleHashChange = () => {
      if (data?.stories && data.stories.length > 0) {
        handleScrollToAnchor();
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [data?.stories]);

  const dateRangeString = (startString: string, endString: string): string => {
    const startDate = parseISO(startString);
    const endDate = parseISO(endString);
    if (isSameYear(startDate, endDate)) {
      if (isSameMonth(startDate, endDate)) {
        return `${format(startDate, 'd.')} - ${format(endDate, 'd. MMMM yyyy')}`;
      }
      return `${format(startDate, 'd. MMMM')} - ${format(endDate, 'd. MMMM yyyy')}`;
    }
    return `${format(startDate, 'd. MMMM yyyy')} - ${format(endDate, 'd. MMMM yyyy')}`;
  };

  if (error) {
    return <NotFoundPage />;
  }

  return (
    <div>
      <div className="jumbotron header-image relative">
        <img
          src={data ? imgUrl(data.trip.id, fullImage(data.trip.headerFileName)) : ''}
          alt="Trip header"
          loading="eager"
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: 'center 30%',
          }}
        />
        <div className="header-info relative z-10">
          {data && data.trip && data.trip.startDate && data.trip.endDate && (
            <p id="date-range">{dateRangeString(data.trip.startDate!, data.trip.endDate!)}</p>
          )}
          <h1>{data ? data.trip.title : ''}</h1>
          <div className="relative w-full flex justify-center mt-6">
            <div className="absolute top-1/2 left-[10%] w-[80%] h-px bg-gray-300" />
            {data && data.trip && (
              <a href={`/profile/${data.trip.ownerId}`} className="inline-block size-12 z-10">
                <img
                  title="Visit Profile"
                  src={
                    `https://media.${process.env.REACT_APP_HOST}/users/${data.trip.ownerId}` +
                    `-thumbnail.jpg?v=${Date.now()}`
                  }
                  className="rounded-full w-full h-full object-cover border-2 border-white"
                  loading="eager"
                  onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  fetchpriority="high"
                />
              </a>
            )}
          </div>

          <div className="header-info-details">{data?.trip?.description ?? ''}</div>
        </div>
      </div>
      <div className="mt-16 w-[90%] mx-auto">
        {data &&
          data.stories
            .sort((a, b) => {
              const dateA = new Date(a.date).getTime();
              const dateB = new Date(b.date).getTime();
              return dateB - dateA;
            })
            .map((story) => {
              const anchor = slugify(story.title, {
                lower: true,
                strict: true,
              }).slice(0, 50);
              return (
                <StoryBox
                  key={story.id}
                  anchor={`${anchor}`}
                  story={story}
                  linkSecret={linkSecret}
                  interactions={interactions}
                  profile={props.user}
                  isOwner={isOwner}
                />
              );
            })}
      </div>
      <footer>
        <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-between lg:px-8">
          <p className="mt-8 text-center text-sm/6 text-gray-600 md:order-1 md:mt-0">
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
